import { RoleSwitchItems } from "@/components/RoleSwitchItems";
import React, { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronDown, LogOut, UserRound, Upload, Trash2, Loader2, MessageSquareText } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FeedbackModal } from "@/components/FeedbackModal";
import { ShareButton } from "@/components/ShareDialog";

const API = process.env.REACT_APP_BACKEND_URL;

const initials = (name = "") =>
  name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() || "").join("") || "U";

const roleLabel = (r = "") =>
  ({ tenant_admin: "School Admin", contributor: "Contributor", admin: "Super User", super_admin: "Super User" }[(r || "").toLowerCase()] || (r || "User").replace(/_/g, " "));

/**
 * Standardized user menu for the School (tenant) Diagnostic workspace.
 * Uses header-based auth (x-tenant-id / x-user-id) — NOT the session cookie —
 * because school users authenticate via the diag_session localStorage token.
 * Click-to-open only, with My Profile (photo) + Sign Out.
 */
export const SchoolUserMenu = ({ user, headers, onSignOut, accent = "#0b1220" }) => {
  const [photoUrl, setPhotoUrl] = useState(user?.profile_photo_url || null);
  const [dlg, setDlg] = useState(false);
  const [fbOpen, setFbOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const name = user?.full_name || user?.email || "User";
  const photo = photoUrl ? `${API}${photoUrl}` : null;

  const persist = (url) => {
    setPhotoUrl(url);
    try {
      const s = JSON.parse(localStorage.getItem("diag_session") || "null");
      if (s?.user) { s.user.profile_photo_url = url; localStorage.setItem("diag_session", JSON.stringify(s)); }
    } catch { /* ignore */ }
  };

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!["image/png", "image/jpeg", "image/jpg", "image/webp"].includes(f.type)) { toast.error("Use a PNG, JPG, or WebP image."); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error("Image must be under 5 MB."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await axios.post(`${API}/api/assets/tenant-profile-photo`, fd, { headers });
      persist(r.data.profile_photo_url);
      toast.success("Profile photo updated");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  const removePhoto = async () => {
    setBusy(true);
    try { await axios.delete(`${API}/api/assets/tenant-profile-photo`, { headers }); persist(null); toast.success("Photo removed"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not remove"); }
    finally { setBusy(false); }
  };

  const Avatar = ({ size = 32 }) => (
    photo ? (
      <img src={photo} alt={`${name} profile photo`} data-testid="school-user-avatar-img"
        className="rounded-full object-cover shrink-0" style={{ width: size, height: size }} />
    ) : (
      <div data-testid="school-user-avatar-initials"
        className="rounded-full grid place-items-center font-semibold shrink-0 border"
        style={{ width: size, height: size, backgroundColor: "#e6f4f8", color: "#00698a", borderColor: "#bfe3ee", fontSize: size * 0.4 }}>
        {initials(name)}
      </div>
    )
  );

  return (
    <>
      <div className="flex items-center gap-2">
        <ShareButton authHeaders={headers} className="hidden sm:inline-flex" />
        <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid="school-user-menu"
            aria-label={`Account: ${name}, ${roleLabel(user?.role)}`}
            className="flex items-center gap-2 rounded-lg px-2 py-1.5 min-h-[44px] hover:bg-black/5 transition-colors text-left">
            <Avatar size={36} />
            <div className="min-w-0 hidden sm:block">
              <div className="text-[12px] font-semibold text-[#111827] truncate max-w-[140px]">{name}</div>
              <div className="text-[10px] uppercase tracking-wider text-[#6b7280]">{roleLabel(user?.role)}</div>
            </div>
            <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="bottom" align="end" className="w-[290px] max-w-[92vw] max-h-[80vh] overflow-y-auto">
          <DropdownMenuLabel className="flex items-center gap-2">
            <Avatar size={28} />
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate">{name}</div>
              <div className="text-[11px] text-slate-500">{roleLabel(user?.role)}</div>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <RoleSwitchItems />
          <DropdownMenuItem data-testid="school-menu-my-profile" onClick={() => setDlg(true)}>
            <UserRound className="h-4 w-4 mr-2" /> My Profile
          </DropdownMenuItem>
          <DropdownMenuItem data-testid="school-menu-give-feedback" onClick={() => setFbOpen(true)}>
            <MessageSquareText className="h-4 w-4 mr-2" /> Give Us Feedback
          </DropdownMenuItem>
          <DropdownMenuItem data-testid="school-menu-sign-out" onClick={onSignOut} className="text-red-600 focus:text-red-600">
            <LogOut className="h-4 w-4 mr-2" /> Sign Out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      </div>

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="max-w-sm" data-testid="school-my-profile-dialog">
          <DialogHeader>
            <DialogTitle>My Profile</DialogTitle>
            <DialogDescription>{name} · {roleLabel(user?.role)}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-2">
            <Avatar size={96} />
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={onPick} data-testid="school-profile-photo-input" />
            <div className="flex gap-2">
              <Button size="sm" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="school-profile-photo-upload-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-1" />{photo ? "Replace" : "Upload"} Photo</>}
              </Button>
              {photo && (
                <Button size="sm" variant="outline" className="text-red-600" onClick={removePhoto} disabled={busy} data-testid="school-profile-photo-remove-btn">
                  <Trash2 className="h-4 w-4 mr-1" /> Remove
                </Button>
              )}
            </div>
            <p className="text-[11px] text-slate-400 text-center">PNG, JPG, or WebP · square recommended · max 5 MB</p>
          </div>
        </DialogContent>
      </Dialog>

      <FeedbackModal open={fbOpen} onOpenChange={setFbOpen} authHeaders={headers} portalType="School User" />
    </>
  );
};

export default SchoolUserMenu;
